import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { LabeledValue } from '../../../interfaces';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { MediaPlayerFile } from '../../../interfaces/media-player-store.interface';
import { VideoResolution, VideoViewerService } from '../../media-player/components/video-viewer/video-viewer.service';
import { MediaPlayerHelperService } from '../../media-player/services/media-player-helper.service';

export enum SettingOptionType {
  RESOLUTION = 'mediaPlayer_settings_quality',
  PLAYBACK_RATE = 'mediaPlayer_settings_playbackSpeed',
}

const playbackRates: number[] = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

@Component({
  selector: 'viewer-settings-button',
  templateUrl: './viewer-settings-button.component.html',
  styleUrl: './viewer-settings-button.component.scss',
  standalone: false,
})
export class ViewerSettingsButtonComponent {
  @ViewChild('dropdownRef') dropdownRef!: NgbDropdown;

  private readonly videoViewerService: VideoViewerService = inject(VideoViewerService);
  private readonly helperService: MediaPlayerHelperService = inject(MediaPlayerHelperService);

  mode: InputSignal<'video' | 'audio'> = input<'video' | 'audio'>('video');
  item: InputSignal<MediaPlayerFile | null> = input<MediaPlayerFile | null>(null);
  resolution: InputSignal<string> = input<string>('720');
  playbackRate: InputSignal<number> = input<number>(1);
  instanceId: InputSignal<string> = input<string>('');
  isFullscreen: InputSignal<boolean> = input<boolean>(false);

  activeOptionType: WritableSignal<SettingOptionType | null> = signal<SettingOptionType | null>(null);
  optionTypes: SettingOptionType[] = [SettingOptionType.PLAYBACK_RATE, SettingOptionType.RESOLUTION];

  playbackRateItems: LabeledValue<number>[] = playbackRates.map((rate: number) => ({
    value: rate,
    text: rate === 1 ? 'Normal' : `${rate}x`,
  }));

  audioSpeedItems: LabeledValue<number>[] = playbackRates.map((rate: number) => ({
    value: rate,
    text: `${rate}x`,
  }));

  resolutions: Signal<VideoResolution[]> = computed(() => {
    const item: MediaPlayerFile | null = this.item();
    const videoType: string = this.helperService.getVideoType(item);
    if (!item || !videoType) {
      return [];
    }

    return this.videoViewerService.buildResolutionArray(this.item(), item.signedURL, videoType) as VideoResolution[];
  });

  has4KResolution: Signal<boolean> = computed(() => {
    return this.resolutions().some((item: VideoResolution) => item.res === '2160') ?? false;
  });

  resolutionText: Signal<string> = computed(() => {
    const item: VideoResolution | undefined = this.resolutions().find(
      (item: VideoResolution) => item.res === this.resolution()
    );
    return item ? item.label : '';
  });

  playbackRateText: Signal<string> = computed(() => {
    return this.playbackRateItems.find((item: LabeledValue<number>) => item.value === this.playbackRate())?.text;
  });

  playbackRateChange: OutputEmitterRef<number> = output<number>();
  resolutionChange: OutputEmitterRef<VideoResolution> = output<VideoResolution>();
  protected readonly SettingOptionType: typeof SettingOptionType = SettingOptionType;

  public setResolution(resolution: VideoResolution): void {
    this.resolutionChange.emit(resolution);
    this.activeOptionType.set(null);
    this.dropdownRef.close();
  }

  public setOptionType(optionType: SettingOptionType | null): void {
    this.activeOptionType.set(optionType);
  }

  public setPlaybackRate(playbackRate: number): void {
    this.playbackRateChange.emit(playbackRate);
    this.activeOptionType.set(null);
    this.dropdownRef.close();
  }
}
